<template>
  <div
    :aria-busy="isLoading"
    class="topMenu sticky top-0 z-10 w-full border-b border-b-[--color-neutral-a4] bg-[color:var(--color-neutral-a6)]"
  >
    <div class="topMenu-navbar-wrapper mx-auto flex justify-center md:min-w-[700px] lg:min-w-[930px]">
      <div class="topMenu-navbar flex max-w-full gap-[26px] overflow-x-auto md:min-w-[700px] lg:min-w-[930px]">
        <router-link
          to="/careers"
          :class="{ active: $route.path === '/careers' || $route.path === '/careers/' }"
          class="top-menu-item relative whitespace-nowrap py-[18px] text-center font-medium leading-6 transition-all after:absolute after:left-0 after:top-[calc(100%-3px)] after:h-[3px] after:w-full"
        >
          <span
            class="text-center before:block before:h-0 before:font-semibold before:opacity-0 before:content-[attr(data-name)]"
            :data-name="$t('pages.careers.overview')"
          >
            {{ $t("pages.careers.overview") }}
          </span>
        </router-link>

        <div v-if="unitedStatesLocation" class="us-popover flex items-center gap-3">
          <VcPopover
            v-model:open="isPopoverOpen"
            class=""
            :class="{ active: $route.path.startsWith(unitedStatesLocation.permalink) }"
            disable-trigger-events
          >
            <template #trigger="{ toggle }">
              <router-link
                class="top-menu-item usa relative whitespace-nowrap py-[18px] text-center font-medium leading-6 transition-all after:absolute after:left-0 after:top-[calc(100%-3px)] after:h-[3px] after:w-full"
                :class="{ active: isRouteActive(unitedStatesLocation.permalink) }"
                :to="unitedStatesLocation.permalink"
                size="lg"
                variant="link"
                @click="onUSMenuItemClicked"
              >
                {{ unitedStatesLocation.label }}
              </router-link>
              <span
                class="flex cursor-pointer items-center whitespace-nowrap font-medium leading-6"
                tabindex="0"
                role="button"
                :aria-label="isPopoverOpen ? 'Close submenu' : 'Open submenu'"
                :aria-expanded="isPopoverOpen"
                @click.stop="toggle()"
                @keydown.enter.prevent="toggle()"
                @keydown.space.prevent="toggle()"
                @keydown.esc.prevent="isPopoverOpen = false"
              >
                <VcIcon :name="isPopoverOpen ? 'chevron-up' : 'chevron-down'" :size="14" class="" />
              </span>
            </template>

            <!-- This is the content of the popover - our options -->
            <template #content>
              <div
                class="us-popover-list flex w-[145px] flex-col items-start border border-[--color-neutral-a4] bg-[--color-white]"
              >
                <VcButton
                  v-for="location in unitedStatesLocations"
                  :key="location.key"
                  class="us-popover-list__item"
                  :class="{ active: isRouteActive(location.permalink) }"
                  :to="location.permalink"
                  size="lg"
                  variant="link"
                  @click="onUSMenuItemClicked"
                  @keydown.esc.prevent="isPopoverOpen = false"
                  >{{ location.label }}</VcButton
                >
              </div>
            </template>
          </VcPopover>
        </div>
        <router-link
          v-for="location in otherCountryLocations"
          :key="location.key"
          :to="location.permalink"
          :class="{ active: isRouteActive(location.permalink) }"
          class="top-menu-item relative whitespace-nowrap py-[18px] text-center font-medium leading-6 transition-all after:absolute after:left-0 after:top-[calc(100%-3px)] after:h-[3px] after:w-full"
        >
          <span
            class="text-center before:block before:h-0 before:font-semibold before:opacity-0 before:content-[attr(data-name)]"
            :data-name="location.label"
          >
            {{ location.label }}
          </span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useCareersLocations } from "@/shared/thorlabs/careers/composables/useCareersLocations";
import { normalizeCareersLocationValue } from "@/shared/thorlabs/careers/utils/locationVisibility";

const route = useRoute();
const { countryLocations, unitedStatesLocations, isLoading, load } = useCareersLocations();
const unitedStatesLocation = computed(() =>
  countryLocations.value.find((location) => normalizeCareersLocationValue(location.country) === "united states"),
);
const otherCountryLocations = computed(() =>
  countryLocations.value.filter((location) => normalizeCareersLocationValue(location.country) !== "united states"),
);

const isPopoverOpen = ref(false);
const topMenu = ref<HTMLElement | null>(null);
let lastScrollY = window.scrollY;
let headerHeight = 0;

const handleScroll = () => {
  const currentScrollY = window.scrollY;

  const scrollingUp = currentScrollY < lastScrollY;
  if (topMenu.value) {
    topMenu.value.style.top = scrollingUp ? `${headerHeight}px` : "0px";
  }

  lastScrollY = currentScrollY;
};

const updateHeaderHeight = () => {
  const header = document.querySelector(".header") as HTMLElement | null;
  headerHeight = header?.offsetHeight ?? 0;
};

onMounted(async () => {
  await load();
  topMenu.value = document.querySelector(".topMenu");

  updateHeaderHeight();
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("resize", updateHeaderHeight);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", updateHeaderHeight);
});

function onUSMenuItemClicked() {
  //Close the popover after item is selected
  isPopoverOpen.value = false;
}

function isRouteActive(permalink: string) {
  return route.path.replace(/\/$/, "") === permalink.replace(/\/$/, "");
}
</script>

<style lang="scss">
.careers-page {
  .topMenu-navbar {
    @media (max-width: 767px) {
      @apply gap-[18px];
    }
    &:before,
    &:after {
      @apply content-[''] min-w-5 block;

      @media (max-width: 767px) {
        @apply min-w-0;
      }
    }
  }

  .topMenu {
    transition: top 0.3s ease-in-out;
  }
  .topMenu-navbar {
    .active,
    .active .top-menu-item {
      @apply font-bold text-[var(--color-primary-a1)] no-underline after:bg-[color:var(--color-primary-a1)];
    }

    .vc-popover.active a {
      svg {
        @apply text-[color:var(--color-primary-a1)];
      }
    }

    @-moz-document url-prefix() {
      scrollbar-width: thin;
    }

    &::-webkit-scrollbar {
      @apply h-2 rounded-xl bg-[color:var(--color-neutral-a6)];
    }
    &::-webkit-scrollbar-thumb {
      @apply bg-[color:var(--color-neutral-a4)]  rounded-xl;
    }
    &::-webkit-scrollbar-track {
      @apply rounded-xl;
    }
  }

  .vc-input__input {
    &::placeholder {
      @apply text-[--color-neutral-a1];
    }
  }

  .select-location {
    position: relative;
    z-index: 1; /* Ensure the input is above other items if needed */
  }

  // US popover
  .us-popover {
    &:has(.vc-popover.active) {
      .usa {
        @apply after:absolute after:left-0 after:top-[calc(100%-3px)] after:h-[3px] after:w-full no-underline after:bg-[color:var(--color-primary-a1)] last:mr-0 text-[color:var(--color-primary-a1)] font-bold;
      }
    }

    .vc-popover__trigger {
      @apply relative whitespace-nowrap text-center font-medium leading-6 transition-all flex items-center gap-3;

      span {
        @apply cursor-pointer;
      }
    }
    &.active {
      .vc-popover__trigger {
        @apply after:absolute after:left-0 after:top-[calc(100%-3px)] after:h-[3px] after:w-full no-underline after:bg-[color:var(--color-primary-a1)] last:mr-0;
      }
    }

    .vc-popover__content {
      @apply text-center before:block before:h-0 before:font-semibold before:opacity-0 before:content-[attr(data-name)] ml-[6px] top-[68px] md:top-[60px] #{!important};

      &.position-bottom {
        .us-popover-list {
          @apply rounded-t-none rounded-b-md  #{!important};
        }
      }

      &.position-top {
        @apply top-[60px] #{!important};
        .us-popover-list {
          @apply rounded-t-none rounded-b-md  #{!important};
        }
      }
    }
    .us-popover-list__item {
      @apply text-base leading-6 py-2.5 px-3 hover:bg-[--color-neutral-a5] w-full text-left text-[--color-neutral-a1] font-medium border-b border-[--color-neutral-a4] last-of-type:border-b-0;

      &.active {
        @apply bg-[--color-neutral-a5] font-semibold;
      }

      .vc-button__content {
        @apply justify-start;
      }
    }
  }
}
</style>
